import { useEffect, useRef } from "react";

/* ========================================================================
   STEP 0. 파형 렌더링 고정 상수
   - WIDTH_PX / HEIGHT_PX: 화면 표시와 캡처에 공통으로 쓰는 최종 캔버스 크기
   - SAMPLE_RATE: 디코딩된 파형 PCM payload가 따라야 하는 샘플레이트
   ======================================================================== */
const WIDTH_PX = 650;
const HEIGHT_PX = 100;
const SAMPLE_RATE = 96000;
const PLOT_PADDING = {
  top: 14,
  right: 14,
  bottom: 18,
  left: 42,
};

/**
 * STEP 1. Waveform 컴포넌트 진입점
 * 목적
 * - data URL로 인코딩된 Float32 PCM payload를 입력받음
 * - 소스가 바뀔 때 한 번만 디코딩함
 * - time / align / density가 바뀌면 고정 크기 파형을 다시 그림
 *
 * Props
 * - pcmDataUrl: Float32 PCM 바이트가 담긴 data URL
 * - time: 초 단위 기준 시각
 * - align: "center" 또는 "start" 정렬 모드
 * - captureRef: 캡처/내보내기 흐름에서 쓰는 선택적 wrapper ref
 * - samplesPerPixel: x축 1픽셀에 압축해서 담을 원본 PCM 밀도
 */
export default function Waveform({
  pcmDataUrl,
  time = 0,
  align = "center",
  captureRef,
  samplesPerPixel = 30,
}) {

  const canvasRef = useRef(null);
  const pcmRef = useRef(null);

  /* ======================================================================
     STEP 2. 소스가 바뀔 때마다 PCM payload를 한 번만 디코딩
     파이프라인
     - STEP 2-1. data URL을 ArrayBuffer로 변환
     - STEP 2-2. 버퍼를 Float32 샘플로 해석
     - STEP 2-3. 디코딩된 PCM을 pcmRef에 캐시
     - STEP 2-4. 현재 props 기준으로 즉시 렌더링
     ====================================================================== */
  useEffect(() => {
    let cancelled = false;
    if (!pcmDataUrl) return;

    (async () => {
      const pcm = await dataUrlToFloat32Array(pcmDataUrl);
      if (cancelled) return;

      pcmRef.current = pcm;
      renderAtTime(canvasRef.current, pcm, time ?? 0, align, samplesPerPixel);
    })();

    return () => {
      cancelled = true;
    };
  }, [pcmDataUrl]);

  /* ======================================================================
     STEP 3. 뷰포트 관련 props가 바뀌면 다시 렌더링
     - 이미 디코딩된 PCM은 pcmRef에서 재사용
     - 스크러빙, 정렬, 줌 밀도 변경 시 이 경로로 갱신
     ====================================================================== */
  useEffect(() => {
    if (!canvasRef.current || !pcmRef.current) return;
    renderAtTime(
      canvasRef.current,
      pcmRef.current,
      time ?? 0,
      align,
      samplesPerPixel,
    );
  }, [time, align, samplesPerPixel]);

  /* ======================================================================
     STEP 4. 고정 크기 wrapper와 canvas 렌더링
     - 바깥 div는 부모 컴포넌트에서 그대로 캡처 가능
     - report 이미지 export 시 canvas 너비를 항상 동일하게 유지
     ====================================================================== */
  return (
    <div style={{ maxWidth: `${WIDTH_PX}px` }} ref={captureRef}>
      <canvas
        ref={canvasRef}
        width={WIDTH_PX}
        height={HEIGHT_PX}
        style={{
          display: "block",
          width: `${WIDTH_PX}px`,
          height: `${HEIGHT_PX}px`,
          background: "#0b0b0b",
          border: "1px solid #333",
        }}
      />
    </div>
  );
}

/* ========================================================================
   HELPER 섹션
   - HELPER 1~2: 소스 파싱 보조 함수
   - HELPER 3: 다운샘플링 보조 함수
   - HELPER 4: 렌더링 파이프라인 조정 함수
   - HELPER 5: 최종 canvas 그리기 함수
   ======================================================================== */

/* ------------------------------------------------------------------------
   HELPER 1. data URL -> ArrayBuffer
   - 브라우저 data URL에서 바이너리 PCM payload를 가져옴
   ------------------------------------------------------------------------ */
async function dataUrlToArrayBuffer(dataUrl) {
  const res = await fetch(dataUrl);
  return res.arrayBuffer();
}

/* ------------------------------------------------------------------------
   HELPER 2. data URL -> Float32Array
   - 가져온 바이트를 Float32 PCM 샘플로 다시 해석
   ------------------------------------------------------------------------ */
async function dataUrlToFloat32Array(dataUrl) {
  const ab = await dataUrlToArrayBuffer(dataUrl);
  return new Float32Array(ab);
}

function clampNumber(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function formatSecondsLabel(seconds) {
  const safeSeconds = Math.max(Number(seconds) || 0, 0);
  if (safeSeconds < 1) {
    return `${(safeSeconds * 1000).toFixed(0)} ms`;
  }

  if (safeSeconds < 10) {
    return `${safeSeconds.toFixed(2)} s`;
  }

  return `${safeSeconds.toFixed(1)} s`;
}

function buildWaveformRenderMetrics({
  start,
  end,
  center,
  align,
  samplesPerPixel,
}) {
  const plotWidth = WIDTH_PX - PLOT_PADDING.left - PLOT_PADDING.right;
  const plotHeight = HEIGHT_PX - PLOT_PADDING.top - PLOT_PADDING.bottom;
  const startTime = start / SAMPLE_RATE;
  const endTime = end / SAMPLE_RATE;
  const focusTime = center / SAMPLE_RATE;
  const visibleDuration = Math.max(endTime - startTime, 0);
  const focusRatio =
    align === "start" || visibleDuration <= 0
      ? 0
      : clampNumber((focusTime - startTime) / visibleDuration, 0, 1);

  return {
    plotX: PLOT_PADDING.left,
    plotY: PLOT_PADDING.top,
    plotWidth,
    plotHeight,
    startTime,
    endTime,
    focusTime,
    focusRatio,
    visibleDuration,
    samplesPerPixel,
  };
}

/* ------------------------------------------------------------------------
   HELPER 3. Segment -> WIDTH_PX 값
   목적
   - 추출한 PCM segment를 압축해서 렌더링 x축 1픽셀당 값 1개가 되도록 만듦
   - 기본 모드는 캡처 결과를 또렷하게 유지하기 위해 nearest 샘플링 사용
   - phase는 각 픽셀이 자신의 논리 그룹 안에서 어느 지점을 샘플링할지 결정
   ------------------------------------------------------------------------ */
function sampleDownToWidth(
  samples,
  samplesPerPixel,
  { mode = "nearest", phase = "center" } = {},
) {
  const out = new Float32Array(WIDTH_PX);
  const sampleCount = samples.length;
  if (!sampleCount) return out;

  // 잘못된 density 값이 들어와도 안전하게 막는다.
  const step = Math.max(0, Number(samplesPerPixel) || 0);
  if (step === 0) {
    return out;
  }

  // 각 논리 그룹 안에서 어떤 지점을 대표 샘플로 쓸지 결정한다.
  let offset = 0;
  if (phase === "center") offset = Math.max(0, (step - 1) * 0.5);
  else if (phase === "end") offset = Math.max(0, step - 1);
  // "start"는 의도적으로 offset = 0을 유지한다.

  for (let px = 0; px < WIDTH_PX; px++) {
    const sampleIndexFloat = px * step + offset;

    if (mode === "linear") {
      // 인접한 두 원본 샘플 사이를 선형 보간한다.
      const i0 = Math.floor(sampleIndexFloat);
      const i1 = i0 + 1;
      const t = sampleIndexFloat - i0;
      const s0 = i0 >= 0 && i0 < sampleCount ? samples[i0] : 0;
      const s1 = i1 >= 0 && i1 < sampleCount ? samples[i1] : s0;
      out[px] = s0 * (1 - t) + s1 * t;
    } else {
      // 기본 경로: 가장 가까운 샘플을 그대로 선택한다.
      let i = Math.round(sampleIndexFloat);
      if (i < 0) i = 0;
      else if (i >= sampleCount) i = sampleCount - 1;
      out[px] = samples[i];
    }
  }

  return out;
}

/* ------------------------------------------------------------------------
   HELPER 4. 렌더링 파이프라인 조정 함수
   파이프라인
   - STEP 4-1. 기준 시간을 PCM 샘플 인덱스로 변환
   - STEP 4-2. WIDTH_PX * samplesPerPixel로 가시 구간 크기 계산
   - STEP 4-3. align 모드에 따라 추출 범위 계산
   - STEP 4-4. PCM 경계를 넘지 않도록 범위 보정
   - STEP 4-5. 화면에 보일 PCM segment 잘라내기
   - STEP 4-6. WIDTH_PX 개수의 대표값으로 다운샘플링
   - STEP 4-7. canvas에 파형 그리기
   ------------------------------------------------------------------------ */
function renderAtTime(canvas, pcm, time, align, samplesPerPixel) {
  if (!canvas || !pcm) return;

  // STEP 4-1. 요청된 초 단위 시간을 PCM 샘플 인덱스로 변환한다.
  const center = Math.floor(time * SAMPLE_RATE);

  // STEP 4-2. 화면에 보이는 구간을 몇 개의 원본 샘플로 채울지 계산한다.
  const windowSamples = WIDTH_PX * Math.max(1, Math.floor(samplesPerPixel));

  // STEP 4-3. 선택된 정렬 모드에 따라 시작/끝 범위를 만든다.
  let start;
  let end;

  if (align === "start") {
    start = center;
    end = start + windowSamples;
  } else {
    const half = Math.floor(windowSamples / 2);
    start = center - half;
    end = start + windowSamples;
  }

  // STEP 4-4. PCM 버퍼 범위를 절대 벗어나지 않도록 보정한다.
  if (start < 0) {
    start = 0;
    end = windowSamples;
  }

  if (end > pcm.length) {
    end = pcm.length;
    start = Math.max(0, end - windowSamples);
  }

  // STEP 4-5. 전체 PCM 버퍼에서 현재 화면에 보일 segment를 잘라낸다.
  const segment = pcm.subarray(start, end);

  // STEP 4-6. segment를 정확히 WIDTH_PX 개 값으로 축약한다.
  const sampled = sampleDownToWidth(segment, samplesPerPixel);

  // STEP 4-7. 고정 출력 canvas에 최종 파형을 그린다.
  drawWaveform(
    canvas,
    sampled,
    buildWaveformRenderMetrics({
      start,
      end,
      center,
      align,
      samplesPerPixel,
    }),
  );
}

/* ------------------------------------------------------------------------
   HELPER 5. 최종 canvas 그리기 함수
   역할
   - 선명한 캡처 결과를 위해 devicePixelRatio 기준으로 backing canvas 크기 조정
   - 수평 기준선을 그림
   - 그 기준선 위에 waveform polyline을 그림
   ------------------------------------------------------------------------ */
function drawWaveform(canvas, values, metrics) {
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;

  canvas.width = Math.round(WIDTH_PX * dpr);
  canvas.height = Math.round(HEIGHT_PX * dpr);
  canvas.style.width = `${WIDTH_PX}px`;
  canvas.style.height = `${HEIGHT_PX}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  ctx.clearRect(0, 0, WIDTH_PX, HEIGHT_PX);
  const {
    plotX,
    plotY,
    plotWidth,
    plotHeight,
    startTime,
    endTime,
    focusTime,
    focusRatio,
    visibleDuration,
    samplesPerPixel,
  } = metrics;
  const midY = plotY + plotHeight / 2;
  const focusX = plotX + plotWidth * focusRatio;
  const yTicks = [1, 0.5, 0, -0.5, -1];
  const xTicks = [
    { ratio: 0, label: formatSecondsLabel(startTime) },
    {
      ratio: 0.5,
      label: formatSecondsLabel(startTime + visibleDuration / 2),
    },
    { ratio: 1, label: formatSecondsLabel(endTime) },
  ];

  const panelGradient = ctx.createLinearGradient(0, 0, 0, HEIGHT_PX);
  panelGradient.addColorStop(0, "#06111f");
  panelGradient.addColorStop(1, "#0a1729");
  ctx.fillStyle = panelGradient;
  ctx.fillRect(0, 0, WIDTH_PX, HEIGHT_PX);

  ctx.fillStyle = "rgba(15, 23, 42, 0.32)";
  ctx.fillRect(plotX, plotY, plotWidth, plotHeight);

  ctx.strokeStyle = "rgba(148, 163, 184, 0.16)";
  ctx.lineWidth = 1;
  yTicks.forEach((tick) => {
    const y = plotY + ((1 - (tick + 1) / 2) * plotHeight);
    ctx.beginPath();
    ctx.moveTo(plotX, y);
    ctx.lineTo(plotX + plotWidth, y);
    ctx.stroke();
  });
  xTicks.forEach((tick) => {
    const x = plotX + plotWidth * tick.ratio;
    ctx.beginPath();
    ctx.moveTo(x, plotY);
    ctx.lineTo(x, plotY + plotHeight);
    ctx.stroke();
  });

  ctx.strokeStyle = "rgba(226, 232, 240, 0.32)";
  ctx.strokeRect(plotX, plotY, plotWidth, plotHeight);

  ctx.strokeStyle = "rgba(96, 165, 250, 0.9)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(plotX, midY);
  ctx.lineTo(plotX + plotWidth, midY);
  ctx.stroke();

  ctx.strokeStyle = "rgba(59, 130, 246, 0.8)";
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(focusX, plotY);
  ctx.lineTo(focusX, plotY + plotHeight);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.fillStyle = "#e2e8f0";
  ctx.font = "600 10px 'Segoe UI'";
  ctx.textBaseline = "top";
  ctx.fillText("WAVEFORM", plotX, 2);

  ctx.fillStyle = "rgba(191, 219, 254, 0.92)";
  ctx.font = "500 9px 'Segoe UI'";
  ctx.textAlign = "right";
  ctx.fillText(`+1.0`, plotX - 6, plotY - 4);
  ctx.fillText(`+0.5`, plotX - 6, plotY + plotHeight * 0.25 - 4);
  ctx.fillText(`0`, plotX - 6, midY - 4);
  ctx.fillText(`-0.5`, plotX - 6, plotY + plotHeight * 0.75 - 4);
  ctx.fillText(`-1.0`, plotX - 6, plotY + plotHeight - 4);

  ctx.textAlign = "left";
  ctx.fillStyle = "rgba(148, 163, 184, 0.95)";
  ctx.fillText("Amp", 6, plotY + plotHeight / 2 - 4);

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  xTicks.forEach((tick) => {
    const x = plotX + plotWidth * tick.ratio;
    ctx.fillStyle = "rgba(191, 219, 254, 0.92)";
    ctx.fillText(tick.label, x, HEIGHT_PX - 4);
  });

  ctx.textAlign = "right";
  ctx.fillStyle = "rgba(148, 163, 184, 0.95)";
  ctx.fillText(
    `Window ${formatSecondsLabel(visibleDuration)} | ${samplesPerPixel} spp | Focus ${formatSecondsLabel(focusTime)}`,
    WIDTH_PX - 8,
    10,
  );

  const waveformStroke = ctx.createLinearGradient(0, plotY, 0, plotY + plotHeight);
  waveformStroke.addColorStop(0, "#7dd3fc");
  waveformStroke.addColorStop(0.5, "#38bdf8");
  waveformStroke.addColorStop(1, "#0ea5e9");

  ctx.save();
  ctx.beginPath();
  ctx.rect(plotX, plotY, plotWidth, plotHeight);
  ctx.clip();

  const areaGradient = ctx.createLinearGradient(0, plotY, 0, plotY + plotHeight);
  areaGradient.addColorStop(0, "rgba(56, 189, 248, 0.32)");
  areaGradient.addColorStop(1, "rgba(56, 189, 248, 0.02)");
  ctx.beginPath();
  ctx.moveTo(plotX, midY);
  for (let x = 0; x < WIDTH_PX; x++) {
    const ratio = WIDTH_PX <= 1 ? 0 : x / (WIDTH_PX - 1);
    const px = plotX + ratio * plotWidth;
    const amplitude = clampNumber(values[x] ?? 0, -1, 1);
    const py = midY - amplitude * (plotHeight / 2 - 2);
    ctx.lineTo(px, py);
  }
  ctx.lineTo(plotX + plotWidth, midY);
  ctx.closePath();
  ctx.fillStyle = areaGradient;
  ctx.fill();

  ctx.beginPath();
  for (let x = 0; x < WIDTH_PX; x++) {
    const ratio = WIDTH_PX <= 1 ? 0 : x / (WIDTH_PX - 1);
    const px = plotX + ratio * plotWidth;
    const amplitude = clampNumber(values[x] ?? 0, -1, 1);
    const py = midY - amplitude * (plotHeight / 2 - 2);
    if (x === 0) {
      ctx.moveTo(px, py);
    } else {
      ctx.lineTo(px, py);
    }
  }
  ctx.strokeStyle = waveformStroke;
  ctx.shadowColor = "rgba(56, 189, 248, 0.38)";
  ctx.shadowBlur = 8;
  ctx.lineWidth = 1.4;
  ctx.stroke();
  ctx.restore();
}
